import { FormEvent, useEffect, useState } from "react";
import { api } from "../lib/api";

type Settings = Record<string, any>;

export function SettingsPage({ csrf }: { csrf: string }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [tab, setTab] = useState<"pbx" | "twilio" | "telnyx" | "sip" | "elevenlabs" | "dialer">("pbx");
  const [error, setError] = useState("");
  const [msg, setMsg] = useState("");
  const [testing, setTesting] = useState("");

  useEffect(() => {
    api
      .getSettings()
      .then((r) => setSettings(r.settings))
      .catch((e) => setError(e.message));
  }, []);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!settings) return;
    setError("");
    setMsg("");
    try {
      const res = await api.saveSettings(settings, csrf);
      setSettings(res.settings);
      setMsg("Settings saved (secrets encrypted at rest)");
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  async function test(provider: string) {
    setTesting(provider);
    setError("");
    setMsg("");
    try {
      await api.testProvider(provider, csrf);
      setMsg(`${provider} credentials OK`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Test failed");
    } finally {
      setTesting("");
    }
  }

  if (!settings) {
    return <p className="muted">Loading settings…</p>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Telephony & voice settings</h1>
          <p>
            Outbound campaigns use the FalconJA PBX and Twilio SIP trunk. Browser FalconPhone never receives
            Twilio credentials. Optional Telnyx / Twilio REST keys remain for fallback.
          </p>
        </div>
      </div>
      {error ? <div className="alert error">{error}</div> : null}
      {msg ? <div className="alert success">{msg}</div> : null}

      <div className="tabs">
        {(
          [
            ["pbx", "FalconJA PBX"],
            ["twilio", "Twilio REST"],
            ["telnyx", "Telnyx"],
            ["sip", "SIP"],
            ["elevenlabs", "ElevenLabs"],
            ["dialer", "Dialer / TTS"]
          ] as const
        ).map(([id, label]) => (
          <button key={id} type="button" className={tab === id ? "active" : ""} onClick={() => setTab(id)}>
            {label}
          </button>
        ))}
      </div>

      <form className="card" onSubmit={onSave}>
        <label>
          <strong>Active provider</strong>
          <select
            value={settings.activeProvider || "twilio"}
            onChange={(e) => setSettings({ ...settings, activeProvider: e.target.value })}
          >
            <option value="auto">Auto (PBX when ready)</option>
            <option value="pbx">FalconJA PBX / Twilio SIP</option>
            <option value="twilio">Twilio REST (fallback)</option>
            <option value="telnyx">Telnyx</option>
            <option value="sip">SIP</option>
          </select>
        </label>

        {tab === "pbx" ? (
          <>
            <h2>FalconJA PBX / Twilio SIP trunk</h2>
            <p className="muted">
              {settings.pbx?.notes ||
                "The browser registers to sip:1000@falconja.com over WSS. Twilio stays behind Asterisk."}
            </p>
            <dl className="form-grid">
              <div>
                <strong>AMI</strong>
                <p>{settings.pbx?.amiConnected ? "Connected" : "Not connected"}</p>
              </div>
              <div>
                <strong>Caller ID</strong>
                <p>{settings.pbx?.callerIdConfigured ? "Configured on server" : "Set TWILIO_PHONE_NUMBER"}</p>
              </div>
              <div>
                <strong>SIP domain</strong>
                <p className="mono">{settings.pbx?.sipDomain || "—"}</p>
              </div>
              <div>
                <strong>Concurrent cap</strong>
                <p>
                  {settings.pbx?.limiter?.active ?? 0}/{settings.pbx?.limiter?.maxConcurrent ?? 20} active ·{" "}
                  {settings.pbx?.limiter?.callsPerSecond ?? 2} cps
                </p>
              </div>
            </dl>
            <button className="btn ghost" type="button" disabled={!!testing} onClick={() => test("pbx")}>
              {testing === "pbx" ? "Testing…" : "Test PBX"}
            </button>
          </>
        ) : null}

        {tab === "twilio" ? (
          <>
            <h2>Twilio</h2>
            <div className="form-grid">
              <label>
                <strong>Account SID</strong>
                <input
                  value={settings.twilio?.accountSid || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, twilio: { ...settings.twilio, accountSid: e.target.value } })
                  }
                />
              </label>
              <label>
                <strong>Auth token</strong>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={settings.twilio?.authToken || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, twilio: { ...settings.twilio, authToken: e.target.value } })
                  }
                  placeholder="•••• or paste new token"
                />
              </label>
              <label>
                <strong>From number (E.164)</strong>
                <input
                  value={settings.twilio?.fromNumber || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, twilio: { ...settings.twilio, fromNumber: e.target.value } })
                  }
                  placeholder="+18765550000"
                />
              </label>
            </div>
            <button className="btn ghost" type="button" disabled={!!testing} onClick={() => test("twilio")}>
              {testing === "twilio" ? "Testing…" : "Test Twilio"}
            </button>
          </>
        ) : null}

        {tab === "telnyx" ? (
          <>
            <h2>Telnyx</h2>
            <div className="form-grid">
              <label>
                <strong>API key</strong>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={settings.telnyx?.apiKey || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, telnyx: { ...settings.telnyx, apiKey: e.target.value } })
                  }
                />
              </label>
              <label>
                <strong>Connection ID</strong>
                <input
                  value={settings.telnyx?.connectionId || ""}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      telnyx: { ...settings.telnyx, connectionId: e.target.value }
                    })
                  }
                />
              </label>
              <label>
                <strong>From number</strong>
                <input
                  value={settings.telnyx?.fromNumber || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, telnyx: { ...settings.telnyx, fromNumber: e.target.value } })
                  }
                />
              </label>
            </div>
            <button className="btn ghost" type="button" disabled={!!testing} onClick={() => test("telnyx")}>
              {testing === "telnyx" ? "Testing…" : "Test Telnyx"}
            </button>
          </>
        ) : null}

        {tab === "sip" ? (
          <>
            <h2>SIP trunk</h2>
            <p className="muted">
              Store your SIP credentials here. Outbound dialing uses your Telnyx Call Control connection or
              Twilio SIP domain that is already linked to this trunk.
            </p>
            <div className="form-grid">
              <label>
                <strong>SIP host</strong>
                <input
                  value={settings.sip?.host || ""}
                  onChange={(e) => setSettings({ ...settings, sip: { ...settings.sip, host: e.target.value } })}
                />
              </label>
              <label>
                <strong>Port</strong>
                <input
                  type="number"
                  value={settings.sip?.port ?? 5060}
                  onChange={(e) =>
                    setSettings({ ...settings, sip: { ...settings.sip, port: Number(e.target.value) } })
                  }
                />
              </label>
              <label>
                <strong>Username</strong>
                <input
                  value={settings.sip?.username || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, sip: { ...settings.sip, username: e.target.value } })
                  }
                />
              </label>
              <label>
                <strong>Password</strong>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={settings.sip?.password || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, sip: { ...settings.sip, password: e.target.value } })
                  }
                />
              </label>
              <label>
                <strong>Transport</strong>
                <select
                  value={settings.sip?.transport || "udp"}
                  onChange={(e) =>
                    setSettings({ ...settings, sip: { ...settings.sip, transport: e.target.value } })
                  }
                >
                  <option value="udp">UDP</option>
                  <option value="tcp">TCP</option>
                  <option value="tls">TLS</option>
                </select>
              </label>
              <label>
                <strong>From URI / caller ID</strong>
                <input
                  value={settings.sip?.fromUri || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, sip: { ...settings.sip, fromUri: e.target.value } })
                  }
                />
              </label>
              <label>
                <strong>Outbound proxy</strong>
                <input
                  value={settings.sip?.outboundProxy || ""}
                  onChange={(e) =>
                    setSettings({ ...settings, sip: { ...settings.sip, outboundProxy: e.target.value } })
                  }
                />
              </label>
            </div>
            <label>
              <span>
                <input
                  type="checkbox"
                  checked={Boolean(settings.sip?.enabled)}
                  onChange={(e) =>
                    setSettings({ ...settings, sip: { ...settings.sip, enabled: e.target.checked } })
                  }
                />{" "}
                SIP profile enabled
              </span>
            </label>
          </>
        ) : null}

        {tab === "elevenlabs" ? (
          <>
            <h2>ElevenLabs AI voice</h2>
            <p className="muted">
              Optional. When configured, answered calls can play premium ElevenLabs speech instead of carrier
              TTS.
            </p>
            <div className="form-grid">
              <label>
                <strong>API key</strong>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={settings.elevenlabs?.apiKey || ""}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      elevenlabs: { ...settings.elevenlabs, apiKey: e.target.value }
                    })
                  }
                />
              </label>
              <label>
                <strong>Voice ID</strong>
                <input
                  value={settings.elevenlabs?.voiceId || ""}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      elevenlabs: { ...settings.elevenlabs, voiceId: e.target.value }
                    })
                  }
                />
              </label>
              <label>
                <strong>Model ID</strong>
                <input
                  value={settings.elevenlabs?.modelId || "eleven_monolingual_v1"}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      elevenlabs: { ...settings.elevenlabs, modelId: e.target.value }
                    })
                  }
                />
              </label>
            </div>
            <button
              className="btn ghost"
              type="button"
              disabled={!!testing}
              onClick={() => test("elevenlabs")}
            >
              {testing === "elevenlabs" ? "Testing…" : "Test ElevenLabs"}
            </button>
          </>
        ) : null}

        {tab === "dialer" ? (
          <>
            <h2>Dialer defaults</h2>
            <div className="form-grid">
              <label>
                <strong>Default max concurrent</strong>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={settings.dialer?.maxConcurrent ?? 120}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      dialer: { ...settings.dialer, maxConcurrent: Number(e.target.value) }
                    })
                  }
                />
              </label>
              <label>
                <strong>Dial timeout (seconds)</strong>
                <input
                  type="number"
                  min={5}
                  max={120}
                  value={settings.dialer?.dialTimeoutSec ?? 30}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      dialer: { ...settings.dialer, dialTimeoutSec: Number(e.target.value) }
                    })
                  }
                />
              </label>
              <label>
                <strong>Default TTS provider</strong>
                <select
                  value={settings.tts?.provider || "auto"}
                  onChange={(e) =>
                    setSettings({ ...settings, tts: { ...settings.tts, provider: e.target.value } })
                  }
                >
                  <option value="auto">Auto</option>
                  <option value="elevenlabs">ElevenLabs</option>
                  <option value="twilio">Twilio Say / Polly</option>
                  <option value="telnyx">Telnyx Speak</option>
                </select>
              </label>
              <label>
                <strong>Twilio/Polly voice</strong>
                <input
                  value={settings.tts?.voice || "Polly.Joanna"}
                  onChange={(e) =>
                    setSettings({ ...settings, tts: { ...settings.tts, voice: e.target.value } })
                  }
                />
              </label>
            </div>
            <label>
              <strong>Default script</strong>
              <textarea
                value={settings.tts?.defaultScript || ""}
                onChange={(e) =>
                  setSettings({ ...settings, tts: { ...settings.tts, defaultScript: e.target.value } })
                }
              />
            </label>
          </>
        ) : null}

        <div className="row-actions" style={{ marginTop: "1rem" }}>
          <button className="btn" type="submit">
            Save settings
          </button>
        </div>
      </form>
    </div>
  );
}
